/**
 * IFrame Embed Block
 * 
 * A reusable block that renders an iframe. The URL can be mapped to any
 * property field (e.g., custom_eliseai_tour_scheduler_url) or set as a
 * custom value. Supports configurable height, title, and border settings.
 * 
 * Inherits theme styling for its container (padding, border-radius, etc.)
 * via CSS variables.
 */


import type { BlockDefinition, BlockProps } from '../../types';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useBlockToken, EditableBlockToken } from '@vibesites/runtime/react';

// ============================================================================
// Component
// ============================================================================

function IframeEmbedComponent({ sectionId: _sectionId }: BlockProps) {
  const url = useBlockToken('url');
  const height = useBlockToken('height', { defaultValue: '600' });
  const title = useBlockToken('title', { defaultValue: 'Embedded Content' });
  const borderRadius = useBlockToken('border_radius', { defaultValue: '0' });

  if (!url) {
    return (
      <div
        className="flex items-center justify-center py-16 text-center bg-muted"
        style={{ color: 'var(--vibe-color-text-muted)' }}
      >
        <div>
          <EditableBlockToken
            localName="url"
            type="url"
            label="Embed URL"
            as="p"
            className="text-lg font-medium"
            style={{ fontFamily: 'var(--vibe-font-heading)' }}
          >
            IFrame Embed
          </EditableBlockToken>
          <p className="text-sm mt-1">
            Configure a URL to display embedded content.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="w-full overflow-hidden bg-muted"
      style={{
        borderRadius: `${borderRadius}px`,
      }}
    >
      <iframe
        src={url}
        title={title || 'Embedded Content'}
        width="100%"
        height={height || '600'}
        style={{
          border: 'none',
          display: 'block',
          width: '100%',
        }}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        loading="lazy"
      />
    </div>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

const contentSchema: ComponentContentSchema = {
  slot: 'IframeEmbed',
  displayName: 'IFrame Embed',
  tokens: {
    url: {
      label: 'Embed URL',
      type: 'url',
      description: 'The URL to load in the iframe. Can be mapped to a property field.',
      required: true,
    },
    height: {
      label: 'Height (px)',
      type: 'text',
      defaultValue: '600',
      description: 'Height of the iframe in pixels.',
    },
    title: {
      label: 'Title',
      type: 'text',
      defaultValue: 'Embedded Content',
      description: 'Accessible title for the iframe (for screen readers).',
    },
    border_radius: {
      label: 'Border Radius (px)',
      type: 'text',
      defaultValue: '0',
      description: 'Corner rounding in pixels.',
    },
  },
};

// ============================================================================
// Block Definition
// ============================================================================

export const iframeEmbedBlock: BlockDefinition = {
  id: 'iframe-embed',
  name: 'IFrame Embed',
  description: 'Embed external content via an iframe. Supports tour schedulers, videos, maps, and more.',
  category: 'Embeds',
  component: IframeEmbedComponent,
  contentSchema,
  requirements: {
    designTokens: [
      'color.surface',
      'color.textMuted',
      'font.heading',
    ],
    dataTokens: [],
  },
  tags: ['iframe', 'embed', 'video', 'tour', 'scheduler', 'map', 'external'],
  defaultGridSpan: 12,
};
